/**
 * Provider Service
 * 
 * This service layer provides an abstraction over provider-related API calls.
 * It handles all provider CRUD operations, filtering, and status management.
 * 
 * @module ProviderService
 */

import { getProviders, createProvider, updateProvider } from '../utils/apiClient';

/**
 * Fetches providers based on provided API parameters
 * 
 * @param {Object} apiParams - Already converted API parameters
 * @param {boolean} [apiParams.include_inactive] - Whether to include inactive providers
 * @param {string} [apiParams.specialty] - Specialty filter (future use)
 * @param {string} [apiParams.facility_id] - Facility filter (future use)
 * @returns {Promise<Array>} Array of provider objects
 * 
 * @example
 * const providers = await ProviderService.getAll({ include_inactive: true });
 */
export const getAll = async (apiParams = {}) => {
  try {
    const providers = await getProviders(apiParams);
    return providers || [];
  } catch (error) {
    console.error('Error in ProviderService.getAll:', error);
    throw new Error('Failed to fetch providers. Please try again.');
  }
};

/**
 * Creates a new provider
 * 
 * @param {Object} providerData - Provider data to create
 * @param {string} providerData.fullName - Full name of the provider
 * @param {string} providerData.npi - National Provider Identifier
 * @param {string} providerData.taxId - Tax ID
 * @param {Array} providerData.specialties - Array of specialties
 * @param {Array} providerData.affiliations - Array of facility affiliations
 * @returns {Promise<Object>} Created provider response
 * 
 * @example
 * const result = await ProviderService.create(providerData);
 */
export const create = async (providerData) => {
  try {
    const response = await createProvider(providerData);
    
    if (response.statusCode === 'SUCCESS') {
      return {
        success: true,
        message: response.message || 'Provider created successfully',
        data: response.data
      };
    }
    
    return {
      success: false,
      message: response.message || 'Failed to create provider'
    };
  } catch (error) {
    console.error('Error in ProviderService.create:', error);
    throw new Error('Failed to create provider. Please try again.');
  }
};

/**
 * Updates an existing provider
 * 
 * @param {string} providerId - ID of the provider to update
 * @param {Object} providerData - Updated provider data
 * @returns {Promise<Object>} Update response
 * 
 * @example
 * const result = await ProviderService.update('provider-id', updatedData);
 */
export const update = async (providerId, providerData) => {
  try {
    const response = await updateProvider(providerId, providerData);
    
    if (response.statusCode === 'SUCCESS') {
      return {
        success: true,
        message: response.message || 'Provider updated successfully',
        data: response.data
      };
    }
    
    return {
      success: false,
      message: response.message || 'Failed to update provider'
    };
  } catch (error) {
    console.error('Error in ProviderService.update:', error);
    throw new Error('Failed to update provider. Please try again.');
  }
};

/**
 * Toggles provider status (activate/deactivate)
 * 
 * @param {string} providerId - ID of the provider
 * @param {boolean} isActive - New active status
 * @returns {Promise<Object>} Status toggle response
 * 
 * @example
 * const result = await ProviderService.toggleStatus('provider-id', false);
 */
export const toggleStatus = async (providerId, isActive) => {
  try {
    const response = await updateProvider(providerId, { isActive });
    
    if (response.statusCode === 'SUCCESS') {
      const actionText = isActive ? 'activated' : 'deactivated';
      return {
        success: true,
        message: `Provider ${actionText} successfully`
      };
    }
    
    return {
      success: false,
      message: `Failed to ${isActive ? 'activate' : 'deactivate'} provider`
    };
  } catch (error) {
    console.error('Error in ProviderService.toggleStatus:', error);
    const actionText = isActive ? 'activate' : 'deactivate';
    throw new Error(`Failed to ${actionText} provider. Please try again.`);
  }
};



/**
 * Validates provider data before submission
 * 
 * @param {Object} providerData - Provider data to validate
 * @returns {Object} Validation result
 * 
 * @example
 * const validation = ProviderService.validateData(providerData);
 * if (!validation.isValid) console.log(validation.errors);
 */
export const validateData = (providerData) => {
  const errors = [];
  
  if (!providerData.fullName?.trim()) {
    errors.push('Full name is required');
  }
  
  if (!providerData.npi?.trim()) {
    errors.push('NPI is required');
  } else if (!/^\d{10}$/.test(providerData.npi.replace(/\D/g, ''))) {
    errors.push('NPI must be a 10-digit number');
  }
  
  if (!providerData.taxId?.trim()) {
    errors.push('Tax ID is required');
  }
  
  if (!providerData.specialties || providerData.specialties.length === 0) {
    errors.push('At least one specialty is required');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Formats provider specialties for display
 * 
 * @param {Array} specialties - Array of specialties
 * @returns {string} Formatted specialties string
 * 
 * @example
 * const formatted = ProviderService.formatSpecialties(['Cardiology', 'Internal Medicine']);
 */
export const formatSpecialties = (specialties) => {
  if (!Array.isArray(specialties) || specialties.length === 0) {
    return 'No specialties listed';
  }
  
  if (specialties.length <= 2) {
    return specialties.join(', ');
  }
  
  return `${specialties.slice(0, 2).join(', ')} +${specialties.length - 2} more`;
};

/**
 * Formats provider affiliations for display
 * 
 * @param {Array} affiliations - Array of affiliations
 * @param {Object} facilityMap - Map of facility IDs to names
 * @returns {string} Formatted affiliations string
 * 
 * @example
 * const formatted = ProviderService.formatAffiliations(affiliations, facilityMap);
 */
export const formatAffiliations = (affiliations, facilityMap = {}) => {
  if (!Array.isArray(affiliations) || affiliations.length === 0) {
    return 'No affiliations listed';
  }
  
  const facilityNames = affiliations.map(affiliation => 
    facilityMap[affiliation.facilityId] || affiliation.facilityId
  );
  
  if (facilityNames.length <= 2) {
    return facilityNames.join(', ');
  }
  
  return `${facilityNames.slice(0, 2).join(', ')} +${facilityNames.length - 2} more`;
};

// Export as default object for easier importing
const ProviderService = {
  getAll,
  create,
  update,
  toggleStatus,
  validateData,
  formatSpecialties,
  formatAffiliations
};

export default ProviderService;
